import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLock, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { stepsForOs, stepText } from "../../pages/claudeDevCourse/authoredChecklists.js";
import "./InstallChecklist.css";

/**
 * The checklist widget — CD02 V2602 (install the app) and V2611 (open your own project
 * in it), the only two lessons whose work happens OUTSIDE this app.
 *
 * Written for V2602; V2611 reuses it unchanged, branching on whether the student still
 * has their `index.html` instead of on their platform. The three behaviours below hold
 * for both — read "platform" as "branch".
 *
 * It is deliberately not a test, and says so on screen. See authoredChecklists.js for
 * why V2602 stopped being a drag-to-order canvas; the short version is that the canvas
 * graded sequence recall with the answer printed in the lesson pane, while the real
 * failure modes are the paid plan, the wrong installer, and Git on Windows.
 *
 * Three behaviours carry the design:
 *
 *   PLATFORM BRANCH. The Windows-only Git step is the most common place to get stuck,
 *   and its error never says "install Git". Picking a platform swaps the steps and
 *   the wording within them, so nobody reads instructions for a machine they are not
 *   using. The initial guess comes from the browser and is always overridable — a
 *   wrong guess costs one click, being asked costs everyone one click.
 *
 *   PERSISTENCE. This lesson is interrupted by an installer. Progress is saved under
 *   `checklist:{lessonID}` so leaving and coming back resumes where they were, which
 *   no other lesson in the course needs.
 *
 *   ONE GATE, ON THE PLAN CHECK. Everything else stays locked until that is ticked,
 *   because doing it second is exactly the mistake the lesson exists to prevent.
 */

const storageKeyFor = (lessonID) => `checklist:${lessonID || "unknown"}`;

const readSaved = (lessonID) => {
    try {
        const raw = window.localStorage.getItem(storageKeyFor(lessonID));
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed || typeof parsed !== "object") return null;
        return { os: parsed.os || null, done: Array.isArray(parsed.done) ? parsed.done : [] };
    } catch (e) {
        return null;
    }
};

const writeSaved = (lessonID, state) => {
    try {
        window.localStorage.setItem(storageKeyFor(lessonID), JSON.stringify(state));
    } catch (e) {
        // Quota or privacy mode. The checklist still works for this session; only
        // resuming after a reload is lost, so it must not throw.
    }
};

/**
 * Best guess at the student's platform, used only as the initial selection.
 *
 * `userAgentData` where it exists, `navigator.platform` otherwise — deprecated, but
 * it is the only thing Safari and Firefox offer, and a wrong guess here costs a
 * single click. Anything unrecognised falls back to the first option rather than
 * leaving the list in a state with no steps in it.
 *
 * A checklist whose branch is not a platform at all (V2611 branches on whether the
 * student still has their `index.html`) matches none of these and lands on that
 * fallback, so its FIRST option is its default. Author them in that order.
 */
const guessOs = (options) => {
    const ids = options.map((o) => o.id);
    let raw = "";
    try {
        raw = `${navigator.userAgentData?.platform || ""} ${navigator.platform || ""} ${navigator.userAgent || ""}`.toLowerCase();
    } catch (e) {
        raw = "";
    }
    if (/win/.test(raw) && ids.includes("windows")) return "windows";
    if (/mac|iphone|ipad/.test(raw) && ids.includes("mac")) return "mac";
    if (/linux|android|x11/.test(raw) && ids.includes("linux")) return "linux";
    return ids[0];
};

const InstallChecklist = ({ checklist, lessonID, onComplete, initiallyComplete, solveSignal }) => {
    const { t } = useTranslation();

    const [osId, setOsId] = useState(null);
    const [done, setDone] = useState([]);
    const [hydrated, setHydrated] = useState(false);

    // Load saved progress, or guess the platform on a first visit. Runs per lesson so
    // the widget is reusable if another lesson ever needs a checklist.
    //
    // `checklist` MUST keep a stable identity across renders — buildAuthoredChecklist
    // returns a module-level object, which is what makes that true. If it ever starts
    // building a fresh object, this effect re-runs every render and resets the
    // student's ticks mid-install.
    useEffect(() => {
        const saved = readSaved(lessonID);
        const options = checklist?.os || [];
        setOsId(saved?.os || (options.length ? guessOs(options) : null));
        setDone(saved?.done || []);
        setHydrated(true);
    }, [lessonID, checklist]);

    // Gated on `hydrated` ONLY. It used to also require `osId`, which was invisible
    // while V2602 was the one checklist — it always has a branch, so `osId` is always
    // set — but it meant any future checklist without one silently saved nothing and
    // lost every tick on reload.
    useEffect(() => {
        if (hydrated) writeSaved(lessonID, { os: osId, done });
    }, [hydrated, lessonID, osId, done]);

    const steps = useMemo(() => stepsForOs(checklist, osId), [checklist, osId]);
    const gateId = useMemo(() => checklist?.steps.find((s) => s.gate)?.id || null, [checklist]);

    const gatePassed = !gateId || done.includes(gateId);
    // Completeness is judged against the steps that APPLY. Switching from Windows to
    // Mac must not leave the lesson permanently unfinishable because of a Git step
    // that is no longer on screen; the tick stays saved in case they switch back.
    const doneCount = steps.filter((s) => done.includes(s.id)).length;
    const complete = steps.length > 0 && doneCount === steps.length;

    // Report completion once, and only on a real transition — `complete` is derived,
    // so without the guard every later render would call it again.
    const reportedRef = useRef(false);
    useEffect(() => {
        if (complete && !reportedRef.current) {
            reportedRef.current = true;
            onComplete?.();
        }
    }, [complete, onComplete]);

    const toggle = useCallback((step, locked) => {
        if (locked) return;
        setDone((prev) => (prev.includes(step.id) ? prev.filter((x) => x !== step.id) : [...prev, step.id]));
    }, []);

    const markAllDone = useCallback(() => {
        setDone((prev) => Array.from(new Set([...prev, ...steps.map((s) => s.id)])));
    }, [steps]);

    // "Do it for me" from the navbar's "Stuck?" panel. These steps are self-attested
    // anyway, so ticking them is the whole of what solving this exercise means.
    useEffect(() => {
        if (!solveSignal) return;
        markAllDone();
    }, [solveSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!checklist) {
        return <div className="checklist-empty">{t("checklist.none")}</div>;
    }

    return (
        <div className="checklist-root">
            <p className="checklist-intro">{checklist.intro}</p>

            <div className="checklist-os" role="group" aria-label={checklist.osLabel}>
                <span className="checklist-os-label">{checklist.osLabel}</span>
                {checklist.os.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        className={`checklist-os-btn${o.id === osId ? " is-on" : ""}`}
                        aria-pressed={o.id === osId}
                        onClick={() => setOsId(o.id)}
                    >
                        <span aria-hidden="true">{o.icon}</span> {o.label}
                    </button>
                ))}
            </div>

            <ol className="checklist-steps">
                {steps.map((step, i) => {
                    const isDone = done.includes(step.id);
                    const locked = !gatePassed && step.id !== gateId;

                    // Any of the three may branch on the selected platform. V2611's
                    // two paths diverge on all of them at step 2 — see stepText.
                    const title = stepText(step, "title", osId);
                    const note = stepText(step, "note", osId);

                    return (
                        <li
                            key={step.id}
                            className={`checklist-step${isDone ? " is-done" : ""}${locked ? " is-locked" : ""}`}
                        >
                            <button
                                type="button"
                                className="checklist-tick"
                                onClick={() => toggle(step, locked)}
                                disabled={locked}
                                aria-pressed={isDone}
                                aria-label={title}
                            >
                                {isDone ? <FontAwesomeIcon icon={faCheck} /> : locked ? <FontAwesomeIcon icon={faLock} /> : i + 1}
                            </button>

                            <div className="checklist-body">
                                <div className="checklist-title">
                                    <span className="checklist-icon" aria-hidden="true">{step.icon}</span>
                                    {title}
                                </div>
                                <p className="checklist-text">{stepText(step, "body", osId)}</p>
                                {note && (
                                    <p className="checklist-note">
                                        <FontAwesomeIcon icon={faCircleInfo} /> {note}
                                    </p>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>

            {!gatePassed && <div className="checklist-gate">{checklist.gateNote}</div>}

            <div className="checklist-foot">
                <div className="checklist-progress">
                    {complete || initiallyComplete ? (
                        <span className="checklist-alldone">
                            {/* Authored per lesson — the shared string names the install
                                outcome, which is wrong for any other checklist. */}
                            <FontAwesomeIcon icon={faCheck} /> {checklist.allDone || t("checklist.allDone")}
                        </span>
                    ) : (
                        t("checklist.progress", { done: doneCount, total: steps.length })
                    )}
                </div>

                {!complete && (
                    <button
                        type="button"
                        className="checklist-skip"
                        onClick={markAllDone}
                        title={checklist.alreadyDoneHint}
                    >
                        {checklist.alreadyDone}
                    </button>
                )}
            </div>

            {/* Said plainly rather than implied. Every other exercise in the course
                verifies something; pretending this one does would teach the student
                that a green tick means less than it does elsewhere. */}
            <p className="checklist-attested">{checklist.selfAttested}</p>
        </div>
    );
};

export default InstallChecklist;
